import cn from "classnames"
import { debounce } from "lodash-es"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import ClientData from "@/common/client_data"
import Scrollable from "@/common/components/scrollable"
import { sizeUnit } from "@/common/locales"
import { isTablet } from "@/common/util"
import Calc from "@/common/util/calculator"
import { type SignStateUpdate } from "@/signomatic/app/sign"
import { dataWithPreset, dataWithSignTypeOverride, dataWithFiltersOverride } from "@/signomatic/app/sign_validator"
import Hint from "@/signomatic/components/hint"
import RadioButton from "@/signomatic/components/radio_button"
import SelectBox from "@/signomatic/components/select_box"
import SignTypeLink from "@/signomatic/components/sign_type_link"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { type State } from "@/signomatic/store/reducers"
import { CloseSmall } from "@/skylticons"

const SizeInput: React.FC<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
> = props => {
  if (isTablet()) {
    return <input type="number" pattern="[0-9]*" {...props} />
  }
  return <input type="text" {...props} />
}

const matchData = (w: number, h: number, data: SignData, shape?: SignDataOption): boolean => {
  if (data.max_size) {
    if (data.max_size.w > 0 && w > data.max_size.w) {
      return false
    }

    if (data.max_size.h > 0 && h > data.max_size.h) {
      return false
    }
  }

  if (shape?.name !== "triangle" && ((w === 60 && h === 60) || (w === 61 && h === 61))) {
    return false
  }

  return true
}

const matchFilters = (w: number, h: number, filters: SignDataOptionFilter, state: SignState): boolean => {
  if (filters.min_size && filters.min_size.length > 0) {
    for (const size of filters.min_size) {
      if (!size.filters || size.filters.shape === state.shape) {
        if (size.w > 0 && w < size.w) {
          return false
        }

        if (size.h > 0 && h < size.h) {
          return false
        }
      }
    }
  }

  if (filters.max_size && filters.max_size.length > 0) {
    for (const size of filters.max_size) {
      if (!size.filters || size.filters.shape === state.shape) {
        if (size.w > 0 && w > size.w) {
          return false
        }

        if (size.h > 0 && h > size.h) {
          return false
        }
      }
    }
  }

  if (filters.min_flanged_width) {
    if (["direction_left", "direction_right"].includes(state.shape)) {
      const aw = h / 2
      const flatWidth = w - aw

      if (flatWidth < filters.min_flanged_width) {
        return false
      }
    }
  }

  return true
}

interface SizeToolboxStateProps {
  data: SignData
  signType: string
  signState: SignState
}

type SizeToolboxProps = SizeToolboxStateProps & ToolboxStripItemProps

interface SuggestedSize {
  label: string
  value: {
    w: number
    h: number
  }
}

interface SuggestedThickness {
  label: string
  value: number
}

type SuggestedSizes = SuggestedSize[]
type SuggestedThicknesses = SuggestedThickness[]

const SizeToolbox: React.FC<SizeToolboxProps> = ({ sign, data, signType, signState }) => {
  const [width, setWidth] = useState((sizeUnit === "in" ? signState.widthIn : signState.width)?.toString())
  const [height, setHeight] = useState((sizeUnit === "in" ? signState.heightIn : signState.height)?.toString())
  const { t } = useTranslation()

  const debouncedUpdate = useMemo(
    () =>
      debounce(
        (state: Partial<SignState>) => {
          sign.updateState(state)
        },
        1000,
        { leading: false }
      ),
    [sign]
  )

  const getInitialFixedSize = (data.fixed_sizes || []).length > 0

  const getInitialThicknesses = dataWithPreset(data, signState).suggested_thickness

  const getInitialSuggestions = () => {
    if (getInitialFixedSize) {
      return sizeUnit === "in" ? data.fixed_sizes_in : data.fixed_sizes
    }

    const modifiedData = dataWithSignTypeOverride(
      dataWithPreset(dataWithFiltersOverride(data, signState), signState),
      signType,
      signState
    )

    return sizeUnit === "in" ? modifiedData.suggested_sizes_in : modifiedData.suggested_sizes
  }

  const suggestedSizes = (): SuggestedSizes => {
    const getSuggestions = getInitialSuggestions()
    if (!getSuggestions) {
      return []
    }

    const fixmethod = data.fixmethods.find(item => item.name === signState.fixmethod)
    const shape = data.shapes.find(item => item.name === signState.shape)
    const newSuggestions = ["25x10", "50x30", "60x25", "60x60", "70x30", "80x30", "100x40", "100x100", "150x100"]
    const requirementsForNewSuggestions =
      signState.thickness === 0.8 && !signState.thicknessLegacySizes && signType === "plastic_sign" && !ClientData.admin
    const suggestions = requirementsForNewSuggestions ? newSuggestions : getSuggestions

    return suggestions
      .map(size => {
        const [w, h] = size.split("x").map(x => parseFloat(x))

        if (!w || !h) {
          return {
            label: "",
            value: { w: 0, h: 0 },
          }
        }
        const label = sizeLabel(w, h)
        const value = { w, h }
        return { label, value }
      })
      .filter(size => {
        let { w, h } = size.value

        if (sizeUnit === "in") {
          w = Calc.inToMm(w)
          h = Calc.inToMm(h)
        }

        if (!matchData(w, h, data, shape)) {
          return false
        }

        if (fixmethod && fixmethod.filters) {
          if (!matchFilters(w, h, fixmethod.filters, signState)) {
            return false
          }
        }

        if (shape && shape.filters) {
          if (!matchFilters(w, h, shape.filters, signState)) {
            return false
          }
        }

        return true
      })
  }

  const suggestedThickness = (): SuggestedThicknesses | null => {
    if (!getInitialThicknesses) {
      return null
    }

    return getInitialThicknesses.map(thickness => {
      const value = parseFloat(thickness)
      const label = `${sizeUnit === "in" ? Calc.mmToIn(value, 2) : value} ${sizeUnit}`
      return { label, value }
    })
  }

  const matchingSuggestion = (suggestions?: SuggestedSizes) => {
    if (!getInitialSuggestions()) {
      return null
    }

    if (!suggestions) {
      suggestions = suggestedSizes()
    }

    let { width, height } = signState
    const { widthIn, heightIn } = signState

    if (sizeUnit === "in" && widthIn && heightIn) {
      width = widthIn
      height = heightIn
    }

    for (const option of suggestions) {
      const { w, h } = option.value

      if (w === width && h === height) {
        return option
      }
    }

    return null
  }

  const matchingThickness = (thicknesses?: SuggestedThicknesses | null) => {
    if (!thicknesses) {
      thicknesses = suggestedThickness()
    }

    if (!signState.thickness || !thicknesses) {
      return null
    }

    const value = signState.thickness

    for (const option of thicknesses) {
      const v = option.value

      if (v === value) {
        return option
      }
    }

    return null
  }

  const updateState = (state: Partial<SignState>, debounced = false) => {
    if (sizeUnit === "in") {
      if (state.width) {
        state.width = Calc.inToMm(state.width)
      }

      if (state.height) {
        state.height = Calc.inToMm(state.height)
      }
    }

    if (debounced) {
      debouncedUpdate(state)
    } else {
      sign.updateState(state)
    }
  }

  const sizeLabel = (w: number, h: number) => {
    let paperSize = ""
    if ((w === 148 && h === 210) || (w === 5.8 && h === 8.3)) {
      paperSize = "A5"
    } else if ((w === 210 && h === 297) || (w === 8.3 && h === 11.7)) {
      paperSize = "A4"
    } else if ((w === 297 && h === 420) || (w === 11.7 && h === 16.5)) {
      paperSize = "A3"
    }

    return paperSize ? `${w} x ${h} ${sizeUnit} (${paperSize})` : `${w} x ${h} ${sizeUnit}`
  }

  const [selectedThickness, setSelectedThickness] = useState(matchingThickness())
  const [suggestionSelected, setSuggestionSelected] = useState<boolean | null>(!!matchingSuggestion())
  const [selectedSuggestion, setSelectedSuggestion] = useState(matchingSuggestion())

  const setSizeFromSignState = useCallback(
    ({ update, state }: SignStateUpdate) => {
      if (suggestionSelected) {
        return
      }

      if (!state.widthAuto && update.width) {
        const width = sizeUnit === "in" ? Calc.mmToIn(state.width).toString() : state.width.toString()
        setWidth(width)
      }

      if (!state.heightAuto && update.height) {
        const height = sizeUnit === "in" ? Calc.mmToIn(state.height).toString() : state.height.toString()
        setHeight(height)
      }
    },
    [suggestionSelected]
  )

  useEffect(() => {
    sign.onStateUpdated.subscribe(setSizeFromSignState)

    return () => {
      sign.onStateUpdated.unsubscribe(setSizeFromSignState)
    }
  }, [setSizeFromSignState, sign])

  const updateWidth = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWidth(e.target.value)
    updateState({ width: parseFloat(e.target.value.replace(",", ".")) || 1 })
  }

  const updateHeight = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHeight(e.target.value)
    updateState({ height: parseFloat(e.target.value.replace(",", ".")) || 1 })
  }

  const submitWidth = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      updateState({ width: parseFloat(width?.replace(",", ".") || "") || 1 })
    }
  }

  const submitHeight = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      updateState({ height: parseFloat(height?.replace(",", ".") || "") || 1 })
    }
  }

  const setWidthAuto = () => updateState({ widthAuto: true })

  const setWidthManual = () => updateState({ widthAuto: false })

  const setHeightAuto = () => updateState({ heightAuto: true })

  const setHeightManual = () => updateState({ heightAuto: false })

  const setSizeSuggested = (size: SuggestedSize | null) => {
    if (!size) {
      const options = suggestedSizes()
      size = matchingSuggestion(options) || options[0] || null
    }

    setSuggestionSelected(true)
    setSelectedSuggestion(size)
  }

  useEffect(() => {
    if (suggestionSelected && selectedSuggestion) {
      let { w, h } = selectedSuggestion.value

      if (sizeUnit === "in") {
        w = Calc.inToMm(w)
        h = Calc.inToMm(h)
      }

      sign.updateState({ width: w, height: h })
    }
  }, [suggestionSelected, selectedSuggestion, sign])

  const setSizeManual = () => {
    setSuggestionSelected(null)
    let newWidth = parseFloat(width?.replace(",", ".") || "")
    let newHeight = parseFloat(height?.replace(",", ".") || "")

    if (sizeUnit === "in") {
      newWidth = Calc.inToMm(newWidth)
      newHeight = Calc.inToMm(newHeight)
    }
    sign.updateState({ width: newWidth, height: newHeight })
  }

  const setThickness = (thickness?: SuggestedThickness | null) => {
    if (!thickness) {
      const options = suggestedThickness()
      thickness = matchingThickness(options) || (options && options[0])
    }

    if (thickness) {
      setSelectedThickness(thickness)
      sign.updateState({ thickness: thickness.value })

      if (!suggestionSelected) {
        setSizeManual()
      }
    }
  }

  const hints = () => {
    if (
      (signState.width >= data.max_size.w || signState.height >= data.max_size.h) &&
      (signState.material === "aluminium_lacquered" || ["plastic_sign", "wood_sign", "forex_sign"].includes(signType))
    ) {
      const hintTranslationKey =
        signType === "plastic_sign" || signState.material === "aluminium_lacquered"
          ? "hint_color_and_alu_are_bigger"
          : "hint_alu_and_solid_plast_are_bigger"

      return (
        <Hint
          type="tip"
          translation={`toolbox.${hintTranslationKey}`}
          components={{ a: <SignTypeLink type="aluminium_sign"> alu sign</SignTypeLink> }}
        />
      )
    }

    return null
  }

  const autoToolbox = () => {
    const fixmethod = data.fixmethods.find(method => method.name === signState.fixmethod)
    const shape = data.shapes.find(method => method.name === signState.shape)
    let disabledW = false
    let disabledH = false

    let sign_width = sizeUnit === "in" ? signState.widthIn : signState.width
    let sign_height = sizeUnit === "in" ? signState.heightIn : signState.height

    const contour_sticker = signType === "sticker" && signState.shape === "contour"

    if (signState.forcedWidth) {
      sign_width = Math.round(sizeUnit === "in" ? Calc.mmToIn(signState.forcedWidth) : signState.forcedWidth)
    }

    if (signState.forcedHeight) {
      sign_height = Math.round(sizeUnit === "in" ? Calc.mmToIn(signState.forcedHeight) : signState.forcedHeight)
    }

    if (fixmethod && fixmethod.filters && fixmethod.filters.fixed_size) {
      const { w, h } = fixmethod.filters.fixed_size

      if (w > 0) {
        disabledW = true
      }

      if (h > 0) {
        disabledH = true
      }
    }

    if (shape && shape.filters && shape.filters.fixed_size) {
      const { w, h } = shape.filters.fixed_size

      if (w > 0) {
        disabledW = true
      }

      if (h > 0) {
        disabledH = true
      }
    }

    if (signState.shape === "contour" || signType === "contour_cut") {
      disabledW = true
      disabledH = true
    }

    return (
      <div className="auto-size-toolbox">
        <div className="sizes auto">
          <div className="size">
            <label className="name paragraph-xs bold mb-0" htmlFor="sign_format_width">
              {t("toolbox.width")} ({sizeUnit})
            </label>
            <div className="input">
              <RadioButton
                id="sign_width_fixed"
                name="size-auto-width"
                checked={!signState.widthAuto}
                onChange={setWidthManual}
                disabled={disabledW}
                tabIndex={-1}
              />
              <div className="text-wrapper">
                <SizeInput
                  id="sign_format_width"
                  onBlur={updateWidth}
                  onChange={e => setWidth(e.target.value)}
                  onFocus={setWidthManual}
                  onKeyPress={submitWidth}
                  value={contour_sticker ? sign_width : width}
                  disabled={disabledW}
                />
              </div>
            </div>
            <div className="input automatic">
              <RadioButton
                id="sign_width_automatic"
                name="size-auto-width"
                checked={signState.widthAuto}
                onChange={setWidthAuto}
                disabled={disabledW}
              />
              <label htmlFor="sign_width_automatic">{t("toolbox.automatic")}</label>
            </div>
          </div>
          <div className="size">
            <label className="name paragraph-xs bold mb-0" htmlFor="sign_height_fixed">
              {t("toolbox.height")} ({sizeUnit})
            </label>
            <div className="input">
              <RadioButton
                id="sign_height_fixed"
                name="size-auto-height"
                checked={!signState.heightAuto}
                onChange={setHeightManual}
                disabled={disabledH}
                tabIndex={-1}
              />
              <div className="text-wrapper">
                <SizeInput
                  id="sign_format_height"
                  onBlur={updateHeight}
                  onChange={e => setHeight(e.target.value)}
                  onFocus={setHeightManual}
                  onKeyPress={submitHeight}
                  value={contour_sticker ? sign_height : height}
                  disabled={disabledH}
                />
              </div>
            </div>
            <div className="input automatic">
              <RadioButton
                id="sign_height_automatic"
                name="size-auto-height"
                checked={signState.heightAuto}
                onChange={setHeightAuto}
                disabled={disabledH}
              />
              <label htmlFor="sign_height_automatic">{t("toolbox.automatic")}</label>
            </div>
          </div>
        </div>
        {hints()}
      </div>
    )
  }

  const manualToolbox = () => {
    const options = suggestedSizes()
    const thicknessOptions = suggestedThickness()

    const fixmethod = data.fixmethods.find(method => method.name === signState.fixmethod)
    const shape = data.shapes.find(method => method.name === signState.shape)
    let disabledW = false
    let disabledH = false

    if (fixmethod && fixmethod.filters && fixmethod.filters.fixed_size) {
      const { w, h } = fixmethod.filters.fixed_size

      if (w > 0) {
        disabledW = true
      }

      if (h > 0) {
        disabledH = true
      }
    }

    if (shape && shape.filters && shape.filters.fixed_size) {
      const { w, h } = shape.filters.fixed_size

      if (w > 0) {
        disabledW = true
      }

      if (h > 0) {
        disabledH = true
      }
    }

    let selectedvalue: SuggestedSize | undefined = options[0]

    if (selectedSuggestion) {
      selectedvalue = options.find(
        o => o.value.w === selectedSuggestion?.value.w && o.value.h === selectedSuggestion?.value.h
      )
    }

    let selectedthicknessvalue: SuggestedThickness | null = (thicknessOptions && thicknessOptions[0]) || null

    if (selectedThickness) {
      selectedthicknessvalue = thicknessOptions?.find(o => o.value === selectedThickness?.value) || null

      if (!selectedvalue) {
        selectedvalue = options.find(o => o.value.w === signState.width && o.value.h === signState.height)
      }
    }

    return (
      <div className="manual-size-toolbox">
        {options && (
          <div className="sizes">
            <div className={cn("size", "select", { "radio-hidden": getInitialFixedSize })}>
              <label className="name paragraph-xs bold mb-0" htmlFor="sign_format_size">
                {t(getInitialFixedSize ? "toolbox.sizes" : "toolbox.suggestion")}
              </label>
              <div className="input">
                <RadioButton
                  id="sign_size_predefined"
                  name="size-manual"
                  checked={!!suggestionSelected}
                  onChange={() => setSizeSuggested(selectedSuggestion || options[0] || null)}
                  className="z-10"
                  disabled={disabledH && disabledW}
                />
                <SelectBox
                  id="sign_format_size"
                  className="size-manual-select"
                  leftAlign
                  defaultValue={options[0]}
                  options={options}
                  isSearchable={false}
                  onChange={setSizeSuggested}
                  value={selectedvalue}
                  onMenuOpen={() => setSizeSuggested(selectedSuggestion || options[0] || null)}
                  isDisabled={disabledH && disabledW}
                />
              </div>
            </div>
          </div>
        )}
        {!getInitialFixedSize && (
          <div className="sizes manual">
            <div className="size-radio">
              {options && (
                <div className="input">
                  <RadioButton
                    id="sign_size_fixed"
                    name="size-manual"
                    checked={!suggestionSelected}
                    onChange={setSizeManual}
                    disabled={disabledH && disabledW}
                  />
                </div>
              )}
            </div>
            <div className="size">
              <label className="name left paragraph-xs bold mb-0" htmlFor="sign_format_width">
                {t("toolbox.width")} ({sizeUnit})
              </label>
              <div className="input">
                <div className="text-wrapper">
                  <SizeInput
                    id="sign_format_width"
                    onBlur={updateWidth}
                    onChange={e => setWidth(e.target.value)}
                    onFocus={setSizeManual}
                    onKeyPress={submitWidth}
                    disabled={disabledW}
                    value={width}
                  />
                </div>
              </div>
            </div>
            <div className="divider">
              <CloseSmall />
            </div>
            <div className="size">
              <label className="name left paragraph-xs bold mb-0" htmlFor="sign_format_height">
                {t("toolbox.height")} ({sizeUnit})
              </label>
              <div className="input">
                <div className="text-wrapper">
                  <SizeInput
                    id="sign_format_height"
                    onBlur={updateHeight}
                    onChange={e => setHeight(e.target.value)}
                    onFocus={setSizeManual}
                    onKeyPress={submitHeight}
                    disabled={disabledH}
                    value={signState.forcedHeight || height}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {thicknessOptions && (
          <div className="sizes thickness">
            <div className="size select">
              <div className="name paragraph-xs bold mb-0">{t("toolbox.thickness")}</div>
              <div className="input">
                <SelectBox
                  id="sign_format_thickness"
                  className="size-manual-select"
                  leftAlign
                  defaultValue={thicknessOptions[0]}
                  options={thicknessOptions}
                  isSearchable={false}
                  onChange={setThickness}
                  value={selectedthicknessvalue}
                  onMenuOpen={() => setThickness(selectedThickness || thicknessOptions[0])}
                />
              </div>
            </div>
          </div>
        )}
        {hints()}
      </div>
    )
  }

  const autoSize = data.autosize || (signState && signState.shape === "contour")

  return (
    <>
      {window.innerWidth <= 1023 ? (
        <Toolbox overflow tKey="size">
          <Scrollable scrollToActive scrollActiveToolboxDelay>
            {autoSize ? autoToolbox() : manualToolbox()}
          </Scrollable>
        </Toolbox>
      ) : (
        <Toolbox overflow tKey="size">
          {autoSize ? autoToolbox() : manualToolbox()}
        </Toolbox>
      )}
    </>
  )
}

export default connect(({ signType, signState, currentSignData }: State) => ({
  signType,
  signState,
  data: currentSignData,
}))(SizeToolbox)
